/**
 * Meta repository (key/value).
 *
 * Stored in the `meta` tab as 2 columns: `key`, `value`. No OCC, no soft
 * delete — meta is global per-spreadsheet config. `setValue` upserts: if
 * the key exists, the row is updated in place; otherwise appended.
 *
 * `key` is constrained to the `MetaKey` literal union (`./types.ts`). Bare
 * strings used to slip through and silently corrupt the meta tab when the
 * arguments to `setValue(ctx, key, value)` were swapped — both were
 * `string`. The literal union turns the swap into a compile error.
 */

import { TABS, tabRange } from '../schema';
import { tabRowRange } from '../row-utils';
import type { RepoCtx } from './notifications';
import type { MetaKey, MetaValueOf } from '../types';

/**
 * Shared key/value parsing for raw `meta` tab rows — used both by
 * `metaRepo.list` (fresh read) and by callers that already have a raw
 * batch-read snapshot (e.g. `checkSchemaVersion`'s `preloadedMetaRows`,
 * data-layer spec requirement "Bootstrap Read Deduplication") so the two
 * paths can't silently drift apart.
 */
export function parseMetaRows(rows: string[][]): Array<{ key: string; value: string }> {
  return rows
    .filter((r) => (r[0] ?? '') !== '')
    .map((r) => ({ key: r[0] ?? '', value: r[1] ?? '' }));
}

export const metaRepo = {
  async list(ctx: RepoCtx): Promise<Array<{ key: string; value: string }>> {
    const resp = await ctx.client.valuesGet(
      ctx.spreadsheetId,
      tabRange('meta', { startRow: 2 }),
    );
    const rows = (resp.values ?? []) as string[][];
    return parseMetaRows(rows);
  },

  async getValue<K extends MetaKey>(
    ctx: RepoCtx,
    key: K,
  ): Promise<MetaValueOf<K> | undefined> {
    const all = await metaRepo.list(ctx);
    const found = all.find((r) => r.key === key)?.value;
    return found as MetaValueOf<K> | undefined;
  },

  async setValue<K extends MetaKey>(
    ctx: RepoCtx,
    key: K,
    value: MetaValueOf<K>,
  ): Promise<void> {
    const resp = await ctx.client.valuesGet(
      ctx.spreadsheetId,
      tabRange('meta', { startRow: 2 }),
    );
    const rows = (resp.values ?? []) as string[][];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i] ?? [];
      if ((r[0] ?? '') === key) {
        await ctx.client.valuesUpdate(
          ctx.spreadsheetId,
          tabRowRange('meta', i + 2),
          [[key, value]],
        );
        return;
      }
    }
    // not found — append
    await ctx.client.valuesAppend(ctx.spreadsheetId, `${TABS.meta.name}!A1`, [[key, value]]);
  },
};
